import { Auth, Session, setEnv } from '@huggg/back-bar';
import { Dispatcher } from '@huggg/bar-staff';
import config from 'config';
import { addDays } from 'date-fns';

class AuthService {
  constructor() {
    if (AuthService._instance) {
      return AuthService._instance;
    }

    const LOCAL_STORAGE_KEY = 'huggg_storage_refresh_token';
    this._LOCAL_STORAGE_KEY = LOCAL_STORAGE_KEY;

    const { refreshToken, userToken, userId } =
      this._getStoredData(LOCAL_STORAGE_KEY);
    setEnv(config.env, '3.4.0');
    this._loggedIn = Boolean(refreshToken);

    this._session = new Session(
      config.clientKey,
      config.clientSecret,
      null,
      userId,
      userToken,
      refreshToken,
    );

    this._auth = new Auth(this._session);
    this._dispatcher = new Dispatcher();
    this._sessionState = {
      clientId: this._session.clientId,
      clientSecret: this._session.clientSecret,
      refreshToken: this._session.refreshToken,
      userId: this._session.userId,
      userToken: this._session.userToken,
      userTokenExpiry: this._session.userTokenExpiry,
    };

    this._handleSessionChange = this._handleSessionChange.bind(this);
    this._session.subscribe('sangria_session', this._handleSessionChange);

    AuthService._instance = this;

    return this;
  }

  beginSMSChallenge(number) {
    return this._auth.beginSMSChallenge(number);
  }

  getLoggedInStatus() {
    return this._loggedIn;
  }

  getUserId() {
    return this._sessionState.userId;
  }

  logout() {
    this._loggedIn = false;

    if (this._auth) {
      this._auth.logout();
    }

    this._clearStoredData(this._LOCAL_STORAGE_KEY);
  }

  /**
   * Register a listener for when the user is logged out.
   *
   * @param {Function} callback
   */
  onLogout(callback) {
    if (!callback || callback.constructor !== Function) {
      throw new Error('Provided callback should be of type `Function`.');
    }

    this._dispatcher.on('logout', callback);
  }

  resolveSMSChallenge(code, challengeID, persist = false) {
    return this._auth
      .resolveSMSChallenge(code, challengeID)
      .then((response) => {
        this._loggedIn = true;

        if (persist) {
          this._setStoredData(
            this._LOCAL_STORAGE_KEY,
            response.refresh_token,
            response.access_token,
            response.user_id,
          );
        }
      });
  }

  /**
   * Remove refresh token from local storage if it exists.
   *
   * @param {String} key Index key to target in window.localStorage
   * @returns {void}
   */
  _clearStoredData(key) {
    window.localStorage.removeItem(key);
  }

  /**
   * Get the refresh token from local storage if it exists. Will also expire the
   * existing refresh token if past the expiry date, forcing the user to log in
   * again.
   *
   * @param {String} key Index key to target in window.localStorage
   * @returns {String|NULL}
   */
  _getStoredData(key) {
    let refreshToken = null;
    let userToken = null;
    let userId = null;

    const data = window.localStorage.getItem(key);

    if (data) {
      const {
        refreshToken: rToken,
        userToken: uToken,
        userId: uId,
        expires,
      } = JSON.parse(data);

      if (new Date(expires) > new Date()) {
        refreshToken = rToken;
        userToken = uToken;
        userId = uId;
      } else {
        this.logout();
      }
    }

    return { refreshToken, userId, userToken };
  }

  _handleSessionChange(sessionBag) {
    const shouldLogout = this.__shouldLogout(sessionBag);
    const shouldUpdate = this.__shouldUpdateStorage(
      sessionBag,
      this._sessionState,
    );

    this._sessionState = { ...this._sessionState, ...sessionBag };

    if (shouldLogout) {
      this.logout();
      this._dispatcher.dispatch('logout', this._sessionState);
    } else if (shouldUpdate) {
      this._setStoredData(
        this._LOCAL_STORAGE_KEY,
        sessionBag.refreshToken,
        sessionBag.userToken,
        sessionBag.userId,
      );
    }
  }

  __shouldLogout(sessionBag) {
    if (!sessionBag) {
      return false;
    }

    return (
      (!sessionBag.userToken || !sessionBag.refreshToken) && this._loggedIn
    );
  }

  __shouldUpdateStorage(sessionBag, sessionState) {
    if (!sessionBag || !sessionState) {
      return false;
    }

    return (
      (sessionState.userToken !== sessionBag.userToken ||
        sessionState.refreshToken !== sessionBag.refreshToken) &&
      this._loggedIn
    );
  }

  /**
   * Store the provided refresh token in local storage.
   *
   * @param {String} key Index key to target in window.localStorage
   * @param {String} value Refresh token
   * @returns {void}
   */
  _setStoredData(key, refreshToken, userToken, userId = null) {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        refreshToken,
        userToken,
        userId,
        expires: addDays(new Date(), 1),
      }),
    );
  }
}

export default AuthService;
